import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from './ui/dialog';
import { Button } from './ui/button';

interface CreateFileModalProps {
  onClose: () => void;
  onCreate: (name: string, language: string) => void;
}

const CreateFileModal: React.FC<CreateFileModalProps> = ({
  onClose,
  onCreate,
}) => {
  const [name, setName] = useState('');
  const [language, setLanguage] = useState('javascript');
  const [loading, setLoading] = useState(false);

  const languageToExt: Record<string, string> = useMemo(
    () => ({
      javascript: 'js',
      typescript: 'ts',
      html: 'html',
      css: 'css',
      json: 'json',
      python: 'py',
      java: 'java',
      cpp: 'cpp',
      c: 'c',
      php: 'php',
      ruby: 'rb',
      go: 'go',
      rust: 'rs',
      sql: 'sql',
      markdown: 'md',
      plaintext: 'txt',
    }),
    []
  );

  const enforceExtension = (filename: string, lang: string) => {
    const ext = languageToExt[lang] || 'txt';
    if (!filename) return '';
    const parts = filename.split('.');
    if (parts.length > 1) {
      // Replace the last segment with the desired extension
      parts[parts.length - 1] = ext;
      return parts.join('.');
    }
    return `${filename}.${ext}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    try {
      await onCreate(enforceExtension(name.trim(), language), language);
    } finally {
      setLoading(false);
    }
  };

  const languages = [
    'javascript',
    'typescript',
    'html',
    'css',
    'json',
    'python',
    'java',
    'cpp',
    'c',
    'php',
    'ruby',
    'go',
    'rust',
    'sql',
    'markdown',
    'plaintext',
  ];

  const onLanguageChange = (l: string) => {
    setLanguage(l);
    if (name.trim()) setName(prev => enforceExtension(prev, l));
  };

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <div className='flex items-center justify-between'>
            <DialogTitle>Create New File</DialogTitle>
            <DialogClose asChild>
              <button className='text-muted-foreground hover:text-foreground'>
                <X size={20} />
              </button>
            </DialogClose>
          </div>
        </DialogHeader>
        <form onSubmit={handleSubmit} className='space-y-4'>
          <div>
            <label className='mb-1 block text-sm text-muted-foreground'>
              File Name
            </label>
            <input
              className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring'
              value={name}
              onChange={e => setName(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div>
            <label className='mb-1 block text-sm text-muted-foreground'>
              Language
            </label>
            <select
              className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring'
              value={language}
              onChange={e => onLanguageChange(e.target.value)}
            >
              {languages.map(l => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </div>
          <div className='flex justify-end gap-2'>
            <Button variant='secondary' type='button' onClick={onClose}>
              Cancel
            </Button>
            <Button type='submit' disabled={!name.trim() || loading}>
              {loading ? 'Creating…' : 'Create File'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateFileModal;
